import { ApiResponse } from 'apisauce'
import { call, put, select } from 'redux-saga/effects'

import { Airport, FullRequestAirHistory, ResumeAir } from 'src/types'
import { ApplicationState } from 'src/core/store'
import { ClientNotifyCatch } from 'src/config/bugsnag'
import api from 'src/services/api'
import findBasicApiResponseErrors from 'src/services/findBasicApiResponseErrors'
import i18n from 'src/config/i18n'
import objectToQueryString from 'src/helpers/object-to-query-string'

import * as Actions from './actions'
import sanitize from './sanitize'

const axiosConfig = {
  timeout: 72000 // 1m20min
}

export function* getAir({
  payload: {
    customer_supplier_id,
    date_end,
    date_ini,
    day_end,
    day_ini,
    from_airport,
    num_adults,
    reason_id,
    searchKey,
    searchType,
    to_airport
  }
}: ReturnType<typeof Actions.getAir>) {
  try {
    const { user } = yield select((state: ApplicationState) => state.me)

    const queryString = objectToQueryString({
      customer_supplier: customer_supplier_id,
      date_end,
      date_ini,
      day_end,
      day_ini,
      from_airport,
      itinerary: searchType === 'ROUND_TRIP' ? 'ROUND_TRIP' : 'ONE_WAY',
      num_adults,
      reason_id,
      resume: 'True',
      to_airport
    })

    const response: ApiResponse<{
      resume_air: ResumeAir[]
      message_id: string[]
    }> = yield call(
      api.get,
      `organizations/${user.organization_id}/flights${queryString}`,
      null,
      axiosConfig
    )

    if (response.ok) {
      // @ts-ignore
      const argsError = response.data.args?.[0]?.error
      if (typeof argsError === 'string' && argsError) {
        yield put(Actions.getAirFailure(searchKey, argsError))
        return
      }
      const resume_air = sanitize(response.data.resume_air)
      yield put(
        Actions.getAirSuccess(searchKey, resume_air, response.data.message_id)
      )
      return
    }

    if (response.status === 500) {
      // @ts-ignore
      const reason = response.data?.reasons
      if (typeof reason === 'string' && reason) {
        yield put(Actions.getAirFailure(searchKey, reason))
        return
      }
    }

    const message = findBasicApiResponseErrors(response)
    yield put(Actions.getAirFailure(searchKey, message))
  } catch (err) {
    ClientNotifyCatch('Saga getAir', err)
    const message = i18n.t('common.response.unexpected-error')
    yield put(Actions.getAirFailure(searchKey, message))
  }
}

export function* getAirHistory({
  payload: { request_id }
}: ReturnType<typeof Actions.getAirHistory>) {
  try {
    const {
      user: { organization_id }
    } = yield select((state: ApplicationState) => state.me)

    const {
      data,
      ok
    }: ApiResponse<{ full_request_air: FullRequestAirHistory[] }> = yield call(
      api.get,
      `/organizations/${organization_id}/requests/${request_id}/air_historic`
    )

    if (ok) {
      yield put(
        Actions.getAirHistorySuccess({ historyList: data.full_request_air })
      )
      return
    }

    const message = i18n.t('common.response.unexpected-error')
    yield put(Actions.getAirHistoryFailure(message))
  } catch (err) {
    ClientNotifyCatch('Saga getAirHistory', err)
    const message = i18n.t('common.response.unexpected-error')
    yield put(Actions.getAirHistoryFailure(message))
  }
}

export function* getAirports({
  payload: { name }
}: ReturnType<typeof Actions.getAirports>) {
  try {
    const { user } = yield select((state: ApplicationState) => state.me)

    const { data, ok }: ApiResponse<Airport[]> = yield call(
      api.get,
      `organizations/${user.organization_id}/airports?name=${name}&size=5`
    )

    if (ok) {
      yield put(Actions.getAirportsSuccess(data))
      return
    }

    yield put(Actions.getAirportsFailure())
  } catch (err) {
    ClientNotifyCatch('Saga getAirports', err)
    yield put(Actions.getAirportsFailure())
  }
}
